import { useMemo } from 'react';

// ---------------------------------------------------------------------------
// Types (exported so PassTypes.tsx can import them instead of re-defining)
// ---------------------------------------------------------------------------

export interface RawAttribute {
  name: string;
  value?: unknown;
}

export interface ProcessedOption {
  id: string;
  name: string;
  description: string;
  value: unknown;
}

export interface RawAddOnProduct {
  id: string;
  key?: string;
  masterData?: {
    current?: {
      name?: Record<string, string>;
      description?: Record<string, string>;
      masterVariant?: {
        id?: number;
        prices?: Array<{ value: { centAmount: number; currencyCode: string } }>;
        price?: { value: { centAmount: number; currencyCode: string } };
        attributes?: RawAttribute[];
      };
    };
    staged?: {
      name?: Record<string, string>;
      description?: Record<string, string>;
      masterVariant?: {
        id?: number;
        prices?: Array<{ value: { centAmount: number; currencyCode: string } }>;
        price?: { value: { centAmount: number; currencyCode: string } };
        attributes?: RawAttribute[];
      };
    };
  };
}

export interface ProcessedAddOn {
  id: string;
  key?: string;
  name: string;
  price: { value: { centAmount: number; currencyCode: string } };
  currencyCode: string;
  description: string;
  masterVariantId?: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UsePassProcessorParams {
  rawOptions?: RawAttribute[][];
  providedOptions?: ProcessedOption[];
  rawAddOns?: RawAddOnProduct[];
  providedAddOns?: ProcessedAddOn[];
  activeCurrency?: string;
}

interface UsePassProcessorResult {
  options: ProcessedOption[];
  addOns: ProcessedAddOn[];
}

export function usePassProcessor({
  rawOptions = [],
  providedOptions = [],
  rawAddOns = [],
  providedAddOns = [],
  activeCurrency = 'USD',
}: UsePassProcessorParams): UsePassProcessorResult {
  const options = useMemo(() => {
    if (providedOptions && providedOptions.length > 0) return providedOptions;
    if (!rawOptions || !Array.isArray(rawOptions)) return [];

    return rawOptions.map((sessionGroup, index) => {
      const nameAttr = sessionGroup.find(
        (a) => a.name === 'optionName' || a.name === 'option-name'
      );
      const dateAttr = sessionGroup.find(
        (a) => a.name === 'optionDate' || a.name === 'option-date'
      );

      let dateStr = (dateAttr?.value as string) || '';
      try {
        if (dateStr) {
          const dateObj = new Date(dateStr);
          const day = dateObj.getDate();
          const month = dateObj.toLocaleString('default', { month: 'long' });
          const dayName = dateObj.toLocaleString('default', { weekday: 'long' });
          dateStr = `${day} ${month} (${dayName})`;
        }
      } catch (err) {
        console.error('Failed to parse date for option:', dateStr, err);
      }

      return {
        id: `session-${index}`,
        name: (nameAttr?.value as string) || `Session ${index + 1}`,
        description: dateStr,
        value: sessionGroup,
      };
    });
  }, [rawOptions, providedOptions]);

  const addOns = useMemo(() => {
    if (providedAddOns && providedAddOns.length > 0) return providedAddOns;
    if (!rawAddOns || !Array.isArray(rawAddOns)) return [];

    return rawAddOns.map((p) => {
      // Handle both raw Commercetools (masterData.current) and flattened frontend model
      const data = p?.masterData?.current || p?.masterData?.staged || p || {};
      
      const nameObj = (data as { name?: Record<string, string> }).name;
      const name = nameObj?.['en'] || p?.key || (typeof nameObj === 'string' ? nameObj : 'Add-on');

      type MasterVariantShape = {
        id?: number;
        prices?: Array<{ value: { centAmount: number; currencyCode: string } }>;
        price?: { value: { centAmount: number; currencyCode: string } };
        attributes?: RawAttribute[];
      };
      
      const mv = (data as { masterVariant?: MasterVariantShape }).masterVariant;

      const getPrice = (variant: MasterVariantShape | undefined, curr: string): number => {
        if (!variant) return 0;
        const prices = variant.prices || [];
        const exactMatch = prices.find((p) => p.value?.currencyCode === curr);
        if (exactMatch) return exactMatch.value.centAmount;
        if (variant.price && variant.price.value) return variant.price.value.centAmount;
        if (prices.length > 0) return prices[0].value?.centAmount || 0;
        return 0;
      };

      const priceCent = getPrice(mv, activeCurrency);
      const attributes = mv?.attributes || [];

      const dateAttr = attributes.find((a) =>
        ['sessionDate', 'startDate'].includes(a.name)
      );
      const timeAttr = attributes.find((a) =>
        ['sessionTime', 'startTime'].includes(a.name)
      );
      const endTimeAttr = attributes.find((a) =>
        ['sessionEndTime', 'endTime'].includes(a.name)
      );

      const startValue = dateAttr?.value as string | undefined;
      const timeValue = timeAttr?.value as string | undefined;
      const endTimeValue = endTimeAttr?.value as string | undefined;

      let description = '';
      if (startValue) {
        try {
          const startDate = new Date(startValue);
          if (isNaN(startDate.getTime())) throw new Error('Invalid Date');

          const weekday = startDate.toLocaleString('default', { weekday: 'short' });
          const month = startDate.toLocaleString('default', { month: 'short' });
          const day = String(startDate.getDate()).padStart(2, '0');
          const year = startDate.getFullYear();
          const datePart = `${day} ${month}, ${year} (${weekday})`;
          let timePart = '';

          if (timeValue) {
            const [hours, minutes] = timeValue.split(':');
            const timeDate = new Date();
            timeDate.setHours(parseInt(hours), parseInt(minutes));
            const startTime = timeDate
              .toLocaleString('en', { hour: 'numeric', minute: '2-digit', hour12: true })
              .toLowerCase()
              .replace(' ', '');

            if (endTimeValue) {
              const [endHours, endMinutes] = endTimeValue.split(':');
              const endTimeDate = new Date();
              endTimeDate.setHours(parseInt(endHours), parseInt(endMinutes));
              const endTime = endTimeDate
                .toLocaleString('en', { hour: 'numeric', minute: '2-digit', hour12: true })
                .toLowerCase()
                .replace(' ', '');
              timePart = `${startTime} - ${endTime}`;
            } else {
              timePart = startTime;
            }
          }

          description = timePart ? `${datePart} from ${timePart}` : datePart;
        } catch (err) {
          console.error('Failed to parse date for add-on:', startValue, err);
        }
      }

      if (!description) {
        description =
          (data as { description?: Record<string, string> }).description?.['en'] || '';
      }

      return {
        id: p.id,
        key: p.key,
        name,
        price: { value: { centAmount: priceCent, currencyCode: activeCurrency } },
        currencyCode: activeCurrency,
        description,
        masterVariantId: mv?.id,
      };
    });
  }, [rawAddOns, activeCurrency, providedAddOns]);

  return { options, addOns };
}
